import { useState, useContext, useEffect } from "react";
import { useFormik, Form, FormikProvider } from "formik";
import * as Yup from "yup";
import {
  Grid,
  Button,
  Dialog,
  DialogContent,
  Typography,
  TextField,
  Checkbox,
  Stack,
} from "@mui/material";
import { useMutation } from "@apollo/client";
import "../../Style/dialogstyle.scss";
import { AuthContext } from "../../Context/AuthContext";
import { DialogTitleComp } from "../../Utils/Component";
import { SelectParty, SelectCandidate } from "../Include/DynamicSelect";
import { useGetNextNumberOrderOfCandidate } from "../../Hooks/useGetNextNumberOrderOfCandidate";
import { CREATE_CANDIDATE, UPDATE_CANDIDATE } from "../../Schema/Candidate";

const getPartyFromLocal = () => {
  const data = JSON.parse(window.localStorage.getItem("partyId"));
  return data ? data : { id: "", title: "" };
};

const setPartyFromLocal = (value) => {
  window.localStorage.setItem("partyId", JSON.stringify(value));
};

export default function CandidateForm({
  open,
  editData,
  setRefetch,
  handleClose,
  dialogTitle,
}) {
  // Change Language 
  const { t, setAlert } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const partyData = getPartyFromLocal();
  const [createCandidate] = useMutation(CREATE_CANDIDATE, {
    onCompleted: ({ createCandidate }) => {
      setLoading(false);
      if (createCandidate?.isSuccess === true) {
        setAlert(true, "success", createCandidate?.message);
        handleClose();
        resetForm();
        setRefetch();
      } else {
        setAlert(true, "error", createCandidate?.message);
      }
    },
    onError: ({ message }) => {
      setLoading(false);
      setAlert(true, "error", { messageKh: message, messageEn: message });
    },
  });

  const [updateCandidate] = useMutation(UPDATE_CANDIDATE, {
    onCompleted: ({ updateCandidate }) => {
      setLoading(false);
      if (updateCandidate?.isSuccess === true) {
        setAlert(true, "success", updateCandidate?.message);
        handleClose();
        resetForm();
        setRefetch();
      } else {
        setAlert(true, "error", updateCandidate?.message);
      }
    },
    onError: ({ message }) => {
      setLoading(false);
      setAlert(true, "error", { messageKh: message, messageEn: message });
    },
  });

  //fromik
  const CheckValidation = Yup.object().shape({
    candidateType: Yup.string().required(t("required")),
    numberOrder: Yup.number().nullable().required(t("required")),
    fromParty: Yup.object().shape({
      id: Yup.string().required(t("required")),
      title: Yup.string(),
    }),
    candidateId: Yup.object().shape({
      id: Yup.string().required(t("required")),
      title: Yup.string(),
    }),
  });

  const formik = useFormik({
    initialValues: {
      numberOrder: null,
      candidateId: { id: "", title: "" },
      fromParty: { id: "", title: "" },
      candidateType: "full_rights",
    },
    validationSchema: CheckValidation,
    onSubmit: async (values) => {
      setLoading(true);

      if (dialogTitle === "Update") {
        updateCandidate({
          variables: {
            id: editData?._id,
            input: {
              candidateType: values?.candidateType,
              numberOrder: parseFloat(values?.numberOrder),
              candidateId: values?.candidateId?.id,
              fromParty: values?.fromParty?.id,
            },
          },
        });
      } else {
        createCandidate({
          variables: {
            input: {
              candidateType: values?.candidateType,
              numberOrder: parseFloat(values?.numberOrder),
              candidateId: values?.candidateId?.id,
              fromParty: values?.fromParty?.id,
            },
          },
        });
      }
    },
  });

  const {
    values,
    errors,
    touched,
    handleSubmit,
    setFieldValue,
    getFieldProps,
    resetForm,
  } = formik;



  useEffect(() => {
    if (editData) {
      setFieldValue("numberOrder", editData?.numberOrder);
      setFieldValue("candidateType", editData?.candidateType);

      setFieldValue("candidateId", {
        id: editData?.candidateId?._id,
        title:
          editData?.candidateId?.lastNameKh +
          " " +
          editData?.candidateId?.firstNameKh,
      });
      setFieldValue("fromParty", {
        id: editData?.fromParty?._id,
        title: editData?.fromParty?.partyName,
      });
    }
  }, [editData, open]);


  const { refetch } = useGetNextNumberOrderOfCandidate({
    candidateType: values?.candidateType,
    partyId: values?.fromParty?.id || "",
    setFieldValue,
    dialogTitle,
  });

  useEffect(() => {
    refetch();
  }, [open]);

  useEffect(() => {
    if (dialogTitle === "Create") {
      setFieldValue("fromParty", partyData);
    }
  }, [open, dialogTitle, values?.fromParty?.id]);

  return (
    <Dialog open={open} className="dialog-container" maxWidth="xs" fullWidth>
      <DialogTitleComp
        title={
          dialogTitle === "Create"
            ? t("modal_title_create_candidate")
            : t("modal_title_update_candidate")
        }
        classTitle={"dialog-title"}
        classIcon={"close-icon"}
        handleClose={handleClose}
      />

      <DialogContent dividers>
        <FormikProvider value={formik}>
          <Form noValidate autoComplete="off" onSubmit={handleSubmit}>
            <Grid container rowSpacing={2} columnSpacing={3}>
              <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                <Typography className={t("class-name-field-title")}>
                  {t("thead_number_order")}
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  className="text-field"
                  placeholder={t("thead_number_order")}
                  {...getFieldProps("numberOrder")}
                  error={Boolean(touched.numberOrder && errors.numberOrder)}
                  helperText={touched.numberOrder && errors.numberOrder}
                />
              </Grid>

              <Grid item xs={12}>
                <SelectCandidate
                  buttonAdd={true}
                  onChange={(val) => setFieldValue("candidateId", val)}
                  value={values?.candidateId}
                  error={Boolean(
                    touched.candidateId?.id && errors.candidateId?.id
                  )}
                  helperText={touched.candidateId?.id && errors.candidateId?.id}
                />
              </Grid>

              <Grid item xs={12}>
                <Typography className={t("class-name-field-title")}>
                  {t("thead_from_party")}
                </Typography>
                <SelectParty
                  dialogTitle={partyData?.id ? null : dialogTitle}
                  error={Boolean(touched.fromParty?.id && errors.fromParty?.id)}
                  helperText={touched.fromParty?.id && errors.fromParty?.id}
                  value={values?.fromParty}
                  buttonAdd={false}
                  onChange={(val) => {
                    setPartyFromLocal(val);
                    setFieldValue("fromParty", val);
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                <Typography className={t("class-name-field-title")}>
                  {t("thead_candidate")}
                </Typography>
                <Stack direction="column">
                  <Stack
                    direction="row"
                    alignItems="center"
                    sx={{ cursor: "pointer" }}
                    onClick={() =>
                      setFieldValue("candidateType", "full_rights")
                    }
                  >
                    <Checkbox
                      checked={Boolean(values?.candidateType === "full_rights")}
                    />
                    <Typography
                      className={t("class-name-field-title")}
                      sx={{ marginTop: 1 }}
                    >
                      {t("thead_full_rights")}
                    </Typography>
                  </Stack>

                  <Stack
                    direction="row"
                    alignItems="center"
                    sx={{ cursor: "pointer" }}
                    onClick={() => setFieldValue("candidateType", "backup")}
                  >
                    <Checkbox
                      checked={Boolean(values?.candidateType === "backup")}
                    />
                    <Typography
                      className={t("class-name-field-title")}
                      sx={{ marginTop: 1 }}
                    >
                      {t("thead_backup")}
                    </Typography>
                  </Stack>
                </Stack>
              </Grid>

              <Grid item xs={12}>
                {loading ? (
                  <Button className={t("class-btn-create")} fullWidth>
                    {t("loading")}
                  </Button>
                ) : (
                  <Button
                    className={t("class-btn-create")}
                    fullWidth
                    onClick={handleSubmit}
                  >
                    {dialogTitle === "Create" ? t("create") : t("update")}
                  </Button>
                )}
              </Grid>
            </Grid>
          </Form>
        </FormikProvider>
      </DialogContent>
    </Dialog>
  );
}
